import React, {ReactNode} from 'react';
import {Property} from 'csstype';
import {Coordinate} from '../../types';
import styled from 'styled-components';
import {theme} from '../style';
import {bound} from '../../utils/bound';

const radius = 200;
const stroke = 45;

export interface SimpleProgressRingProps {
  progress?: number;
  backgroundColor: Property.Color;
  highlightColor: Property.Color;
  size: number;
  children?: ReactNode;
  includeLabel?: boolean;
  includePlusMinus?: boolean;
}

export class SvgSimpleProgressRing extends React.Component<
  SimpleProgressRingProps & {coordinate: Coordinate}
> {
  render() {
    const {
      size,
      coordinate: {x, y},
    } = this.props;

    return (
      // weird but true, svg inside svg is perfectly normal and a convenient grouping method
      <ProgressRing
        x={x}
        y={y}
        width={size}
        height={size}
        viewBox={`0 0 ${radius * 2} ${radius * 2}`}>
        <SimpleProgressRing {...this.props} />
      </ProgressRing>
    );
  }
}

export class MarkupSimpleProgressRing extends React.Component<SimpleProgressRingProps> {
  render() {
    const {size} = this.props;

    return (
      <SimpleProgressRingWrapper size={size}>
        <ProgressRing viewBox={`0 0 ${radius * 2} ${radius * 2}`}>
          <SimpleProgressRing {...this.props} />
        </ProgressRing>
        {this.props.children}
      </SimpleProgressRingWrapper>
    );
  }
}

/**
 * Create a capped progress ring including nullable (N/A if null) middle text label
 */
export class SimpleProgressRing extends React.Component<SimpleProgressRingProps> {
  labelMaker(p: number | undefined, includePlusMinus: boolean) {
    if (p === undefined || p === null) {
      return 'N/A';
    }

    const prefix = includePlusMinus && p >= 0 ? '+' : '';
    const prettyNum = bound(p, -100, 100).toFixed(0);
    const suffix = '%';

    return prefix + prettyNum + suffix;
  }

  render() {
    const {
      progress: p,
      backgroundColor,
      highlightColor,
      includePlusMinus = true,
      includeLabel = true,
    } = this.props;

    // dont go any lower than 5 or over 92 (unless 100), it looks funky
    const absP = Math.abs(p || 0);
    const absProgress = p ? (absP >= 100 ? 100 : bound(absP, 5, 92)) : null;

    const label = this.labelMaker(p, includePlusMinus);

    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;

    // we overlap a capped version of the circle -
    // bring them back a tad to compensate for the fact the cap adds
    // to the end of the line that we draw

    const strokeDashoffset = !absProgress
      ? 0
      : circumference - (absProgress / 100) * circumference + (absProgress >= 100 ? 0 : 15);

    return (
      <g>
        <circle
          stroke={backgroundColor}
          fill="transparent"
          strokeWidth={stroke}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        {absProgress && (
          <g>
            <SquaredEdgesCircularLine
              className="progress"
              stroke={highlightColor}
              fill="transparent"
              strokeWidth={stroke}
              strokeDasharray={circumference + ' ' + circumference}
              style={{strokeDashoffset}}
              r={normalizedRadius}
              cx={radius}
              cy={radius}
              isNegative={p ? p < 0 : false}
            />
            <CappedEdgesCircularLine
              className="progress-capped"
              stroke={highlightColor}
              fill="transparent"
              strokeWidth={stroke}
              strokeDasharray={circumference + ' ' + circumference}
              style={{strokeDashoffset}}
              r={normalizedRadius}
              cx={radius}
              cy={radius}
              strokeLinecap={'round'}
              isNegative={p ? p < 0 : false}
            />
          </g>
        )}
        {includeLabel && (
          <ProgressLabel x={radius} y={radius} alignmentBaseline="middle" textAnchor="middle">
            {label}
          </ProgressLabel>
        )}
      </g>
    );
  }
}

const SimpleProgressRingWrapper = styled.div<{size: number}>`
  position: relative;
  display: inline-block;
  width: ${({size}) => `${size}px`};
  height: ${({size}) => `${size}px`};
  vertical-align: top;
`;

const ProgressRing = styled.svg`
  shape-rendering: geometricPrecision;
`;

/**
 * Note:
 *
 * * transition makes the line "grow" out when the value changes
 * * transform-origin makes sure the rotate/scale happens in the middle of the element BUT
 * * transform-box is needed for svg embed because it makes the thing act like markup would
 * * the 90/78 are magic numbers that provide enough of a gap to force the squared edges one side / circular other side
 * * scale (-1, 1) mirrors the entire thing horizontally in case of a negative number
 */

const SquaredEdgesCircularLine = styled.circle<{isNegative: boolean}>`
  transition: stroke-dashoffset 0.35s;
  transform-origin: 50% 50%;
  transform: ${({isNegative}) => (isNegative ? 'scale(-1, 1)' : '') + ' rotate(-90deg)'};
  transform-box: fill-box;
`;

const CappedEdgesCircularLine = styled.circle<{isNegative: boolean}>`
  transition: stroke-dashoffset 0.35s;
  transform-origin: 50% 50%;
  transform: ${({isNegative}) => (isNegative ? 'scale(-1, 1)' : '') + ' rotate(-78deg)'};
  transform-box: fill-box;
`;

const ProgressLabel = styled.text`
  font-size: 60px;
  font-family: ${theme.fonts.primaryFontFamily};
`;
